import { Fragment } from "react";
import { mergeClasses } from "@fluentui/react-components";
import { useGroupedWorkspaceDashboardStyles } from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.styles";
import { IReadOnlyLogTableProps } from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.types";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { AdminPaginationFooter } from "components/adminList/AdminPaginationFooter";

export const ReadOnlyLogTable = <TItem,>({
  items,
  columns,
  getRowId,
  ariaLabel,
  gridTemplateColumns,
  emptyState,
}: IReadOnlyLogTableProps<TItem>): JSX.Element => {
  const styles = useGroupedWorkspaceDashboardStyles();

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination({ items });

  if (items.length === 0) {
    return <Fragment>{emptyState}</Fragment>;
  }

  return (
    <div className={styles.logPanelCard} role="table" aria-label={ariaLabel}>
      <div
        className={styles.logPanelHeaderRow}
        style={{ gridTemplateColumns }}
        role="rowgroup"
      >
        <div role="row" style={{ display: "contents" }}>
          {columns.map((column) => (
            <span
              key={column.columnKey}
              role="columnheader"
              className={styles.logPanelColumnHeader}
            >
              {column.header}
            </span>
          ))}
        </div>
      </div>
      <div role="rowgroup">
        {paginatedItems.map((item, index) => (
          <div
            key={getRowId(item)}
            role="row"
            className={mergeClasses(
              styles.logPanelDataRow,
              index === paginatedItems.length - 1 && styles.logPanelDataRowLast,
            )}
            style={{ gridTemplateColumns }}
          >
            {columns.map((column) => (
              <span
                key={column.columnKey}
                role="cell"
                className={styles.logPanelCell}
              >
                {column.renderCell(item)}
              </span>
            ))}
          </div>
        ))}
      </div>
      {pageCount > 1 && (
        <AdminPaginationFooter
          selectedIndex={selectedPageIndex}
          pageCount={pageCount}
          itemsPerPage={itemsPerPage}
          totalItemsCount={totalItemsCount}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};
